import Header from '../components/Header';
import Footer from '../components/Footer';
import Hero from '../components/sections/Hero';
import { FeatureGrid } from '../components/sections/FeatureGrid';
import TypeWriter from '../components/Typewriter';
import Stats from '../components/Stats';
import { motion } from "framer-motion";
import { Building2, Calendar, DollarSign, Globe, Handshake, Timer } from "lucide-react";
import { Button } from '../components/ui/button';
import ComparisonTable from '../components/ComparisonTable';
import TimeComparison from '../components/TimeComparison';
import Story from '../components/Story';
import { useAuth } from '../contexts/AuthContext';
import PricingPlans from '../components/pricing/PricingPlans';
import { Link } from 'react-router-dom';
import SalaryChart from '../components/SalaryChart';
import JobFunnel from '../components/JobFunnel';
export default function Home() {
  const { user } = useAuth();
  const gridIcons = [
    { icon: <Building2 className="h-12 w-12" />, title: "Docs", description: "Simple, powerful, beautiful." },
    { icon: <Calendar className="h-12 w-12" />, title: "Calendar", description: "See it all in one place." },
    { icon: <Globe className="h-12 w-12" />, title: "Sites", description: "Share with the world." },
  ];
  const gridIconsFeatures = [
    { icon: <Building2 className="h-12 w-12" />, title: "Personality analysis", description: "Advanced AI analysis of your interviewer's background." },
    { icon: <Calendar className="h-12 w-12" />, title: "Personalized prep", description: "Get customized answer strategies." },
    { icon: <Globe className="h-12 w-12" />, title: "Instant research", description: "Generate interview insights in minutes." },
  ];
  const gridIconsHowItWorks = [
    { step: "1", title: "Upload details", description: "Share your resume and the job description you're applying for." },
    { step: "2", title: "Add interviewer info", description: "Provide your interviewer's LinkedIn profile URL or job title and company." },
    { step: "3", title: "Get personalized strategies", description: "Receive tailored interview strategies based on AI analysis." },
  ];
  const gridIconsPainPoints = [
    { icon: <Timer className="h-12 w-12" />, title: "Time", description: "You're spending hours doing research that could be done in minutes." },
    { icon: <DollarSign className="h-12 w-12" />, title: "Money", description: "Your highest paying companies are completely ignoring you." },
    { icon: <Handshake className="h-12 w-12" />, title: "Opportunity", description: "Your competition is taking your dream job because you don't know what you don't know." },
  ];
  
  const professions = [
  'Healthcare',
'Technology',
'Finance',
'Education',
'Manufacturing',
'Retail',
'Hospitality',
'Construction',
'Marketing',
'Transportation',
'Energy',
'Government'
  ];

  return (
    <div className="flex flex-col">
      <Header />
      <main className="container">
        <Hero />

        <section className="px-5 bg-white">
          <img src="/images/hero-report_example.webp" alt="Hero screenshot" className="w-full h-auto rounded-3xl border border-gray-100" />  
        </section>

      {/* Pain Points */}

        <section className="pt-20 bg-white">
          <div className="container px-4 md:px-6 flex flex-col md:flex-row">
            {/* Left Column */}
            <div className="w-full md:w-1/2 p-4">
              <h2 className="text-4xl font-bold sm:text-5xl md:text-6xl ">If you're not sure what to say and when to say it, <span className="text-blue-500">it's costing you...</span></h2>
              <p className="pt-6 text-base leading-normal text-gray-500">For every month it takes you to get hired...you are losing thousands...If you make $5,000 a month and it takes you 4 months to get hired you just lost $20,000! If you make $10,000 a month and it takes you 6 months to get hired you just lost $60,000!
              </p>
            </div>

            {/* Right Column with Icons */}
            <div className="w-full md:w-1/2 p-8">
              <ul className="flex flex-col md:flex-row list-none p-0 m-0 gap-6">
                {gridIconsPainPoints.map((item, index) => (
                  <li key={index} className="flex flex-row md:flex-col items-center md:items-start space-x-4 md:space-x-0 md:space-y-4 w-full md:w-1/3">
                    <div className="bg-white rounded-xl">
                      {item.icon}
                    </div>
                    <span>
                      <h3 className="text-md font-semibold">{item.title}</h3>
                      <p className="text-gray-500">{item.description}</p>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-10 bg-white">
          <div className="container px-4 md:px-6">

            <SalaryChart />
          </div>
        </section>  

        <JobFunnel />

        {/* Introducing */}
        <section className="pt-20 bg-white">
          <div className="container px-4 md:px-6 flex flex-col md:flex-row">
            {/* Left Column */}
            <div className="w-full md:w-1/2 p-4">
              <h2 className="text-4xl font-bold sm:text-5xl md:text-6xl font-bold">Introducing - InterviewAdvantage.AI</h2>
              <p className="pt-4 text-base leading-normal text-gray-500 tracking-normal">
                Combining the most powerful career planning framework with AI to expertly help you get hired.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/signup">
                <Button variant="default" size="lg" className="text-base">
                  Get Started
                </Button>
              </Link>
              </div>
            </div>

            {/* Right Column with hero */}
            <div className="w-full md:w-1/2 p-4">
              <img src="/images/hero-introducing.webp" alt="Introducing InterviewEdge.AI" className="w-full h-auto" />
            </div>
          </div>
        </section>


        {/* How It Works / 3-steps */}

        <section id="how-it-works" className="pt-40 bg-white">
          <div className="container px-4 md:px-6 flex flex-col md:flex-row">
            {/* Left Column */}
            <div className="w-full md:w-1/2 p-4">
              <h2 className="text-4xl font-bold sm:text-5xl md:text-6xl font-bold">How It Works</h2>
              <p className="pt-2 text-base leading-normal text-gray-500 tracking-normal"></p>
            </div>

            {/* Right Column with Icons */}
            <div className="w-full md:w-1/2 p-4">
              <ul className="flex flex-col md:flex-row list-none p-0 m-0 gap-6">
                {gridIconsHowItWorks.map((item, index) => (
                  <li key={index} className="flex flex-row md:flex-col items-center md:items-start space-x-4 md:space-x-0 md:space-y-4 w-full md:w-1/3">
                    <div className="flex items-center justify-center w-14 h-14 border-2 border-black text-black rounded-full text-xl font-bold">
                      {item.step}
                    </div>
                    <span>
                      <h3 className="text-md font-semibold">{item.title}</h3>
                      <p className="text-gray-500">{item.description}</p>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
        
        <section className="px-5 bg-white">
          <img src="/images/hero-how-it-works.webp" alt="Hero screenshot" className="w-full h-auto rounded-3xl border border-gray-100" />  
        </section>

        {/* Social Proof */}
        <section className="pt-40 bg-white">
          <div className="container px-4 md:px-6 flex flex-col md:flex-row">
            {/* Left Column */}
            <div className="w-full md:w-1/2 p-4">
              <h2 className="text-4xl font-bold sm:text-5xl md:text-6xl font-bold">ArcAdvantage is trusted by people working in</h2>
              <div className="text-4xl font-bold sm:text-5xl md:text-6xl font-bold mb-6">
                <TypeWriter 
                  words={professions}
                  typingSpeed={100}
                  deletingSpeed={50}
                  pauseDuration={1500}
                />
              </div>
              <p className="pt-2 text-base leading-normal text-gray-500 tracking-normal">In the last 20 years thousands of professionals have trusted the ArcAdvantage framework to get hired and see an average 18% increase in compensation.</p>
              <div className="space-y-4">
                <p className="pt-2 text-base leading-normal text-gray-500">Trusted by job seekers and career changers at top corporate offices:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  <div className="flex items-center justify-center">
                    <img src="/images/trust-pepsico.svg" alt="PepsiCo" className="h-6 w-auto" />
                  </div>
                  <div className="flex items-center justify-center">
                    <img src="/images/trust-bain-capital.svg" alt="Bain Capital" className="h-5 w-auto" />
                  </div>
                  <div className="flex items-center justify-center">
                    <img src="/images/trust-fiveguys.svg" alt="Five Guys Corporate" className="h-3 w-auto" />
                  </div>
                  <div className="flex items-center justify-center">
                    <img src="/images/trust-lincoln-financial.svg" alt="Lincoln Financial" className="h-7 w-auto filter grayscale opacity-60" />
                  </div>
                  <div className="flex items-center justify-center">
                    <img src="/images/trust-billabong.svg" alt="Billabong Corporate" className="h-20 w-auto filter grayscale opacity-50" />
                  </div>
                  <div className="flex items-center justify-center">
                    <img src="/images/trust-justice.svg" alt="Justice for Girls" className="h-16 w-auto filter grayscale opacity-50" />
                  </div>
                  <div className="flex items-center justify-center">
                    <img src="/images/trust-mens-warehouse.svg" alt="Mens Warehouse Corporate" className="h-14 w-auto filter grayscale opacity-50" />
                  </div>
                  <div className="flex items-center justify-center">
                    <img src="/images/trust-st_michelle_vineyards.svg" alt="St. Michelle Vineyards" className="h-14 w-auto filter grayscale opacity-50" />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column with hero */}
            <div className="w-full md:w-1/2 p-4">
                <img src="/images/hero-businesses.webp" alt="Introducing InterviewAdvantage.AI" className="w-full h-auto" />  
            </div>
          </div>
        </section>

        <Stats />

        
        {/* Features */}
        <section className="pt-20 bg-white">
          <div className="container px-4 md:px-6 flex flex-col md:flex-row">
            {/* Left Column */}
            <div className="w-full md:w-1/2 p-4">
              <h2 className="text-4xl font-bold sm:text-5xl md:text-6xl font-bold">Find everything. Instantly.</h2>
              <p className="pt-4 text-base leading-normal text-gray-500 tracking-normal">No more endless searching. No more guessing. Our proprietary AI solutions and decades of experience are the only interview prep tools you need.</p>
            </div>

            {/* Right Column with Icons */}
            <div className="w-full md:w-1/2 p-4">
              <ul className="flex flex-col md:flex-row list-none p-0 m-0 gap-6">
                {gridIconsFeatures.map((item, index) => (
                  <li key={index} className="flex flex-row md:flex-col items-center md:items-start space-x-4 md:space-x-0 md:space-y-4 w-full md:w-1/3">
                    <div className="bg-white rounded-xl">
                      {item.icon}
                    </div>
                    <span>
                      <h3 className="text-md font-semibold">{item.title}</h3>
                      <p className="text-gray-500">{item.description}</p>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="px-5 pt-4 bg-white">
          <img src="/images/hero-find-everything.webp" alt="Hero screenshot" className="w-full h-auto rounded-3xl" />  
        </section>



        <ComparisonTable />

        <TimeComparison />

        <Story />

        {/* Testimonials */}
        <section className="relative overflow-hidden py-10 md:py-10 bg-background">
        <div className="container px-4 md:px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <h1 className="text-4xl font-bold sm:text-5xl md:text-6xl">
                Customer testimonials
                </h1>
                <p className="max-w-[600px] text-gray-500 md:text-xl">
                See real results that will help you streamline your job search, unlock higher earning potential, and elevate your professional profile.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-xl overflow-hidden">
                <img
                  src="/images/testimonials-hero.webp"
                  alt="Testimonials"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-white py-5 md:py-5 ">
        <div className="container px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Testimonial 3 */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                "Mike provided me with unparalleled guidance and strategies to navigate job opportunities. His coaching on my job search and interview tactics was superb—offering precise advice on phrasing questions, delivering impactful answers, and clearly conveying the value I bring to organizations. Thanks to Mike's insights, I was able to position myself as the number one choice for multiple positions giving me options I wouldn't have had otherwise and securing very strong compensation. If you're looking to advance your career faster and with confidence from every angle, Mike's expertise is truly invaluable."
              </p>
              <div className="flex items-center">
                <img src="/images/testimonial-janice.webp" alt="Janice" className="h-12 w-12 rounded-full mr-4" />
                <div>
                  <h3 className="font-semibold">Janice M.</h3>
                  <p className="text-gray-500">Atlanta, GA | VP</p>
                </div>
              </div>
            </div>

            {/* Testimonial 1 */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                "I urge everyone in an executive capacity or otherwise looking to gain an edge in his or her job search to work with Mike Sudermann and gain the knowledge and advice this extensive system covers. The results I experienced were phenomenal. I was hired into my ideal position and transformed my life with more than a 50% increase in compensation. A total game changer for me and my family. The strategies work. Mike works."
              </p>
              <div className="flex items-center">
                <img src="/images/testimonial-patrick.jpeg" alt="Patrick" className="h-12 w-12 rounded-full mr-4" />
                <div>
                  <h3 className="font-semibold">Patrick</h3>
                  <p className="text-gray-500">Chicago, IL | SVP</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section className="bg-white py-5 md:py-5 ">
        <div className="container px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">

            {/* Testimonial 2 */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                "I cannot recommend Mike enough and his job search accelerator system. It had been about 20 years since I looked for a new job. I was overwhelmed with where to start. How to best write my resume, LinkedIn, interviewing, networking, talking to recruiters, finding hidden positions, etc. I was stressed out. Mike’s system covered everything I needed to regroup and find a senior-level leadership position very quickly"
              </p>
              <div className="flex items-center">
                <img src="/images/testimonial-cindy.jpeg" alt="Cindy" className="h-12 w-12 rounded-full mr-4" />
                <div>
                  <h3 className="font-semibold">Cindy</h3>
                  <p className="text-gray-500">South Carolina | VP</p>
                </div>
              </div>
            </div>


            {/* Testimonial 4 */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                "Mike is a great partner when making a career change. He is very enthusiastic and works with you to hone in on how to best present yourself and your skills during interviews. Highly recommended if you are looking at making a career change and want to end up in the right position with the best possible compensation."
              </p>
              <div className="flex items-center">
                <img src="/images/testimonial-bozidar.jpeg" alt="Bozidar" className="h-12 w-12 rounded-full mr-4" />
                <div>
                  <h3 className="font-semibold">Bozidar T.</h3>
                  <p className="text-gray-500">IT</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>






      {/* 
      REMOVED.
      <section id="pricing" className="py-20 bg-white">
        <div className="container px-4 md:px-6 flex">
          
          <div className="w-full md:w-1/2 p-4">
          <h1 className="text-4xl font-bold sm:text-5xl md:text-6xl">
          Everything you need to get your dream job.
          </h1>
          </div>
        </div>
      </section>      
      <FeatureGrid />  
      
      
      
      */}

      {/* Pricing */}
      <section className="py-20 bg-white" id="pricing">
        <div className="container px-4 md:px-6 flex-col">
          <h1 className="text-3xl md:text-4xl font-bold sm:text-5xl md:text-6xl">
            Get InterviewAdvantage.ai
          </h1>
          <div>
            <p className="pt-4">Go look at all the bonus materials included in your order. If you're out of work one less day, this product pays for itself. And if you do what we tell you, the lifetime value of these tips can exceed a million dollars in compensation. Stop wasting your time and get the Advantage now.</p>
          </div>
        </div>
        <PricingPlans user={user} />        
        <div className="flex justify-center mt-4">
          <Link to="/signup">
            <Button variant="default" size="lg" className="text-base">
              Get Started
            </Button>
          </Link>
        </div>

      </section>

      </main>
      <Footer />
    </div>
  );
}